import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, CircularProgress, IconButton,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useAuth } from '../../hook';
import {
  getGeneralSummary, getGeneralSummaryMovements, getGeneralSummaryReceivables,
} from './clientApi';
import './client.scss';

const COLORS = ['#171717', '#555', '#888', '#b7b7b7', '#d9d9d9', '#6b42ff'];
const pen = (value) => `S/ ${Number(value || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usd = (value) => `USD ${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const readableDate = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
    .format(new Date(year, month - 1, day));
};
const periodName = (cursor, mode) => new Intl.DateTimeFormat('es-PE', mode === 'month'
  ? { month: 'long', year: 'numeric' }
  : { year: 'numeric' }).format(cursor);

function ChangeBadge({ value }) {
  if (value == null) return <span className="client-summary-badge">Sin comparativo</span>;
  const number = Number(value);
  return <span className={`client-summary-badge${number < 0 ? ' client-summary-badge--negative' : ''}`}>
    {number > 0 ? '+' : ''}{number.toFixed(1)}%
  </span>;
}

function Pager({ data, page, onPage }) {
  if (!data || data.pages <= 1) return null;
  return <div className="client-summary-pager">
    <Button disabled={page <= 1} onClick={() => onPage(page - 1)}>Anterior</Button>
    <span>{page} de {data.pages}</span>
    <Button disabled={page >= data.pages} onClick={() => onPage(page + 1)}>Siguiente</Button>
  </div>;
}

export function GeneralSummary() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [mode, setMode] = useState('month');
  const [cursor, setCursor] = useState(() => new Date());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showMovements, setShowMovements] = useState(false);
  const [showReceivables, setShowReceivables] = useState(false);
  const [movementPage, setMovementPage] = useState(1);
  const [receivablePage, setReceivablePage] = useState(1);
  const [movements, setMovements] = useState(null);
  const [receivables, setReceivables] = useState(null);

  const params = useMemo(() => ({
    period: mode,
    year: cursor.getFullYear(),
    ...(mode === 'month' ? { month: cursor.getMonth() + 1 } : {}),
  }), [cursor, mode]);

  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    getGeneralSummary(auth.token, params)
      .then((data) => { if (active) setSummary(data); })
      .catch((err) => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [auth.token, params]);

  useEffect(() => {
    if (!showMovements) return undefined;
    let active = true;
    getGeneralSummaryMovements(auth.token, { ...params, page: movementPage, page_size: 10 })
      .then((data) => { if (active) setMovements(data); })
      .catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [auth.token, movementPage, params, showMovements]);

  useEffect(() => {
    if (!showReceivables) return undefined;
    let active = true;
    getGeneralSummaryReceivables(auth.token, { ...params, page: receivablePage, page_size: 10 })
      .then((data) => { if (active) setReceivables(data); })
      .catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [auth.token, params, receivablePage, showReceivables]);

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setMovementPage(1); setReceivablePage(1);
    setShowMovements(false); setShowReceivables(false);
  };

  const movePeriod = (amount) => {
    setCursor((current) => new Date(
      current.getFullYear() + (mode === 'year' ? amount : 0),
      mode === 'month' ? current.getMonth() + amount : current.getMonth(),
      1,
    ));
    setMovementPage(1); setReceivablePage(1);
    setShowMovements(false); setShowReceivables(false);
  };

  const categories = summary?.breakdowns?.expenses_by_category || [];
  const expenseTotal = categories.reduce((sum, item) => sum + Number(item.total_pen), 0);
  let accumulated = 0;
  const donut = categories.length ? categories.map((item, index) => {
    const start = accumulated;
    accumulated += expenseTotal ? Number(item.total_pen) / expenseTotal * 100 : 0;
    return `${COLORS[index % COLORS.length]} ${start}% ${accumulated}%`;
  }).join(', ') : '#ececec 0 100%';
  const shownMovements = showMovements ? movements?.results : summary?.recent_movements?.results;
  const shownReceivables = showReceivables ? receivables?.results : summary?.receivables?.results;

  return <main className="client-page client-general-summary">
    <header className="client-header">
      <IconButton onClick={() => navigate('/')}><ArrowBackIosNewIcon /></IconButton>
      <h1>Resumen general</h1><span />
    </header>

    <div className="client-summary-mode">
      <Button className={mode === 'month' ? 'is-active' : ''} onClick={() => changeMode('month')}>Mes</Button>
      <Button className={mode === 'year' ? 'is-active' : ''} onClick={() => changeMode('year')}>Año</Button>
    </div>
    <div className="client-month-picker">
      <IconButton onClick={() => movePeriod(-1)}><ChevronLeftIcon /></IconButton>
      <strong>{periodName(cursor, mode)}</strong>
      <IconButton onClick={() => movePeriod(1)}><ChevronRightIcon /></IconButton>
    </div>
    <p className="client-month-help">Por defecto se muestra el periodo actual</p>

    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {loading || !summary ? <div className="client-empty"><CircularProgress size={30} /></div> : <>
      <p className="client-summary-section-label">Resumen del {mode === 'month' ? 'mes' : 'año'}</p>
      <section className="client-summary-kpis">
        <article>
          <div><span>Ingresos cobrados</span><ChangeBadge value={summary.kpis.change.collections_pct} /></div>
          <strong>{pen(summary.kpis.collections.pen)}</strong>
          <strong className="client-summary-kpis__usd">{usd(summary.kpis.collections.usd)}</strong>
          <small>Consolidado: {pen(summary.kpis.collections.consolidated_pen)}</small>
        </article>
        <article>
          <div><span>Gastos del periodo</span><ChangeBadge value={summary.kpis.change.expenses_pct} /></div>
          <strong>{pen(summary.kpis.expenses.total_pen)}</strong>
          <small>{summary.kpis.expenses.count} movimientos en PEN</small>
        </article>
        <article className={Number(summary.kpis.cash_result_pen) < 0 ? 'is-negative' : 'is-positive'}>
          <div><span>Flujo neto disponible</span><ChangeBadge value={summary.kpis.change.cash_result_pct} /></div>
          <strong>{pen(summary.kpis.cash_result_pen)}</strong>
          <small>Caja disponible menos gastos</small>
        </article>
        <article>
          <div><span>Ventas contratadas</span><ChangeBadge value={summary.kpis.change.sales_pct} /></div>
          <strong>{pen(summary.kpis.sales.total_pen)}</strong>
          <small>{summary.kpis.sales.count} ventas no canceladas</small>
        </article>
      </section>

      <section className="client-summary-strip">
        <div><span>Pendiente por cobrar al cierre</span><strong>{pen(summary.kpis.receivables.total_pen)}</strong></div>
        <small>{summary.kpis.receivables.count} cuentas · Detracciones del periodo: {pen(summary.kpis.collections.detraction_pen)}</small>
      </section>

      <section className="client-summary-panel">
        <div className="client-summary-panel__title"><strong>Gastos por categoría</strong></div>
        <div className="client-expense-breakdown">
          <div className="client-expense-donut" style={{ background: `conic-gradient(${donut})` }}>
            <span>{pen(summary.kpis.expenses.total_pen)}</span>
          </div>
          <div className="client-expense-legend">
            {categories.map((item, index) => <div key={item.category}>
              <i style={{ background: COLORS[index % COLORS.length] }} />
              <span>{item.category}</span>
              <strong>{expenseTotal ? `${(Number(item.total_pen) / expenseTotal * 100).toFixed(0)}%` : '0%'}</strong>
            </div>)}
            {!categories.length && <small>Sin gastos en este periodo.</small>}
          </div>
        </div>
      </section>

      <section className="client-summary-panel">
        <div className="client-summary-panel__title"><strong>Fuentes de ingresos</strong><span>Canal de adquisición</span></div>
        <div className="client-income-sources">
          {(summary.breakdowns.income_by_acquisition_channel || []).map((item) => {
            const maximum = Number(summary.breakdowns.income_by_acquisition_channel[0]?.total_pen || 1);
            return <div key={item.channel}>
              <span>{item.channel}</span>
              <i><b style={{ width: `${Number(item.total_pen) / maximum * 100}%` }} /></i>
              <strong>{pen(item.total_pen)}</strong>
            </div>;
          })}
          {!summary.breakdowns.income_by_acquisition_channel.length && <small>Sin ingresos en este periodo.</small>}
        </div>
      </section>

      <section className="client-summary-panel">
        <div className="client-summary-panel__title">
          <strong>Últimos movimientos</strong>
          <Button onClick={() => { setShowMovements((value) => !value); setMovementPage(1); }}>
            {showMovements ? 'Ver resumen' : 'Ver todos'}
          </Button>
        </div>
        <div className="client-movement-list">
          {(shownMovements || []).map((item) => <div key={item.key}>
            <span className={`client-movement-icon client-movement-icon--${item.type}`}>
              {item.type === 'income' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
            </span>
            <div>
              <strong>{item.type === 'income' ? (item.counterparty || 'Cliente sin nombre') : item.concept}</strong>
              <small>
                {item.type === 'income'
                  ? `Pago de venta · ${item.installment_number ? `Cuota ${item.installment_number}` : 'Sin cuota asignada'}`
                  : (item.counterparty || 'Sin contraparte')}
                {' · '}{readableDate(item.date)}
              </small>
            </div>
            <b className={item.type === 'income' ? 'is-income' : 'is-expense'}>
              {item.type === 'income' ? '+' : '-'} {item.currency === 'USD' ? usd(item.amount_original) : pen(item.amount_original)}
            </b>
          </div>)}
        </div>
        {showMovements && <Pager data={movements} page={movementPage} onPage={setMovementPage} />}
      </section>

      <section className="client-summary-panel">
        <div className="client-summary-panel__title">
          <strong>Cuentas pendientes</strong>
          <Button onClick={() => { setShowReceivables((value) => !value); setReceivablePage(1); }}>
            {showReceivables ? 'Ver resumen' : 'Ver todas'}
          </Button>
        </div>
        <div className="client-receivable-list">
          {(shownReceivables || []).map((item) => <div key={item.sale_id}>
            <span>{Math.min(Math.max(item.days_open, 0), 99)}d</span>
            <div><strong>{item.client || 'Sin cliente'}</strong><small>{item.primary_service || 'Sin servicio'} · {readableDate(item.issue_date)}</small></div>
            <b>{pen(item.pending_pen)}</b>
          </div>)}
          {!(shownReceivables || []).length && <small>No hay cuentas pendientes.</small>}
        </div>
        {showReceivables && <Pager data={receivables} page={receivablePage} onPage={setReceivablePage} />}
      </section>

      {summary.kpis.payables.count > 0 && <section className="client-summary-strip client-summary-strip--payables">
        <div><span>Cuentas por pagar registradas</span><strong>{pen(summary.kpis.payables.total_pen)}</strong></div>
        <small>{summary.kpis.payables.count} compras aún no marcadas como pagadas</small>
      </section>}
    </>}
  </main>;
}
